import { useCallback } from 'react'
import { defaultWpMenu } from 'layouts/shop/WpHeader/defaultWpMenu'

/**
 * Hook that provides the WordPress header/footer menu.
 *
 * The menu used to be fetched live from a Firebase Realtime Database
 * (https://uniprest-magic-test.europe-west1.firebasedatabase.app/) so it could
 * be updated without a deploy. That database lives on a free Google account
 * whose quota has been exhausted, so the integration is dead and unreliable:
 * when it errors it spams error toasts, and if it ever "comes back to life"
 * with stale or empty data it would silently overwrite the menu (potentially
 * wiping it entirely) with no deploy and no control over timing.
 *
 * The Firebase integration has therefore been disabled. The menu is now served
 * statically from `defaultWpMenu`, which is the source of truth. To change the
 * menu, edit defaultWpMenu.js and deploy.
 *
 * NOTE: This is unrelated to the maintenance-mode / forced-refresh Firebase
 * integration in utils/firebase.js, which is a separate Firebase project and
 * remains active.
 *
 * The return shape is kept identical to the previous implementation so callers
 * (WpMenuContext / WpMenuItems / FooterMenu) need no changes, and so the live
 * fetch can be restored later if a paid Firebase is set up.
 *
 * @returns {Object} An object containing:
 *   - menuItems: The static menu object ({ headerMenu, footerMenu })
 *   - loading: Always false
 *   - error: Always null
 *   - refreshMenuItems: No-op (kept for API compatibility)
 */
const useWpMenuItems = () => {
  // Firebase fetch disabled — see file header. Serve the static menu.
  const refreshMenuItems = useCallback(() => {}, [])

  return {
    menuItems: defaultWpMenu,
    loading: false,
    error: null,
    refreshMenuItems
  }
}

export default useWpMenuItems
